/**
 * The signed-in user's credits: balance, the packs this node sells, recent
 * history, and a `buy` that hands off to Stripe Checkout.
 *
 * Gated by the feature_credits node toggle: with it off the backend doesn't
 * mount /api/credits at all, so nothing is fetched and the defaults are
 * returned. featureCredits is off-until-known, so a node that has credits on
 * fetches once /info has resolved. Gathering guests have no Keycloak session
 * (isAuthenticated is false) and so never fetch either — they are never billed.
 *
 *   const { balance, packs, buy, refresh } = useCredits();
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNodeInfo } from './use_node_info';
import { useApi } from './use_api';

const EMPTY = { balance: 0, packs: [], recent: [], enforce: false, perMinute: 1 };

export function useCredits() {
    const api = useApi();
    const { isAuthenticated } = useAuth();
    const { featureCredits } = useNodeInfo();
    const [state, setState] = useState(EMPTY);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const enabled = featureCredits && isAuthenticated;

    const refresh = useCallback(async () => {
        if (!enabled) { setState(EMPTY); return null; }
        setLoading(true);
        try {
            const data = await api.get('/api/credits/me');
            const next = {
                balance: data?.balance ?? 0,
                packs: data?.packs || [],
                recent: data?.recent_ledger || [],
                enforce: !!data?.enforce,
                perMinute: data?.credits_per_participant_minute ?? 1,
            };
            setState(next);
            setError(null);
            return next;
        } catch (err) {
            // A guest or an unapproved account gets a 403 here: that is "no
            // credits surface", not an error worth surfacing.
            if (err?.status !== 403) setError(err);
            return null;
        } finally {
            setLoading(false);
        }
    }, [api, enabled]);

    useEffect(() => { refresh(); }, [refresh]);

    // Starts a Stripe Checkout for one of the node's packs and navigates to it.
    // The success URL lands back on /account, where the Credits card picks the
    // ?credits=purchased marker up and re-fetches (the webhook lags a little).
    const buy = useCallback(async (priceId) => {
        const data = await api.post('/api/credits/checkout', {
            price_id: priceId,
            success_url: `${window.location.origin}/account?credits=purchased`,
            cancel_url: `${window.location.origin}/account`,
        });
        if (!data?.checkout_url) throw new Error('Could not start checkout');
        window.location.href = data.checkout_url;
    }, [api]);

    return { ...state, enabled, loading, error, refresh, buy };
}

export default useCredits;
